import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi, SystemStatus } from '../../api/admin'
import { Mail, AlertTriangle, ClipboardList, Settings, Wrench, RefreshCw, GitMerge } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'

export function SystemStatusTab() {
  const queryClient = useQueryClient()

  const { data: status, isLoading, error } = useQuery<SystemStatus>({
    queryKey: ['system-status'],
    queryFn: adminApi.getSystemStatus,
    refetchInterval: 30000,
    retry: (failureCount, err: any) => {
      if (err?.response?.status === 401) return false
      return failureCount < 2
    },
  })

  const reprocessMutation = useMutation({
    mutationFn: adminApi.reprocessPending,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['system-status'] })
      alert(data.message)
    },
    onError: () => {
      alert('Errore durante il re-processing')
    },
  })

  const rerouteMutation = useMutation({
    mutationFn: adminApi.rerouteCases,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['system-status'] })
      alert(data.message)
    },
    onError: () => {
      alert('Errore durante il re-routing')
    },
  })

  const fixStuckMutation = useMutation({
    mutationFn: adminApi.fixStuckEmails,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['system-status'] })
      alert(data.message)
    },
    onError: () => {
      alert('Errore durante il fix email bloccate')
    },
  })

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Caricamento stato sistema...</p>
      </div>
    )
  }

  if (error) {
    const isAuthError = (error as any)?.response?.status === 401
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>
            {isAuthError
              ? 'Sessione scaduta. Effettua nuovamente il login.'
              : 'Errore caricamento stato sistema'}
          </AlertDescription>
        </Alert>
        <p className="text-sm text-muted-foreground mt-2">
          {isAuthError ? (
            <a href="/login" className="text-primary hover:underline">
              Vai al login →
            </a>
          ) : (
            (error as Error).message
          )}
        </p>
      </div>
    )
  }

  if (!status) {
    return (
      <div className="p-6">
        <p className="text-danger">Errore caricamento stato sistema</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-lg font-semibold text-foreground">Stato Sistema</h2>

      <div className="grid grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-4">
            <h3 className="text-md font-medium text-foreground mb-3 flex items-center gap-1.5">
              <Mail className="size-4" /> Email Processing
            </h3>
            <div className="space-y-2">
              {Object.entries(status.emails).map(([key, value]) => (
                <div key={key} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{key}:</span>
                  <span className={`font-medium ${key === 'new' && value > 0 ? 'text-danger' : 'text-foreground'}`}>
                    {value}
                  </span>
                </div>
              ))}
              {status.emails_stuck_count > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-danger flex items-center gap-1">
                    <AlertTriangle className="size-3" /> Stuck (&gt;30min):
                  </span>
                  <span className="font-medium text-danger">{status.emails_stuck_count}</span>
                </div>
              )}
            </div>
            {status.emails.new > 0 && (
              <div className="mt-4 space-y-2">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => fixStuckMutation.mutate()}
                  disabled={fixStuckMutation.isPending}
                >
                  {fixStuckMutation.isPending ? 'Fixing...' : (
                    <span className="flex items-center gap-1.5">
                      <Wrench className="size-3.5" /> Fix Stuck Emails (NEW → PENDING_VET)
                    </span>
                  )}
                </Button>
                <Button
                  className="w-full"
                  onClick={() => reprocessMutation.mutate()}
                  disabled={reprocessMutation.isPending}
                >
                  {reprocessMutation.isPending ? 'Processing...' : (
                    <span className="flex items-center gap-1.5">
                      <RefreshCw className="size-3.5" /> Re-process Pending
                    </span>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <h3 className="text-md font-medium text-foreground mb-3 flex items-center gap-1.5">
              <ClipboardList className="size-4" /> Cases Status
            </h3>
            <div className="space-y-2">
              {Object.entries(status.cases).map(([key, value]) => (
                <div key={key} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{key}:</span>
                  <span className="font-medium text-foreground">{value}</span>
                </div>
              ))}
              {status.cases_no_specialty > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-orange-600 flex items-center gap-1">
                    <AlertTriangle className="size-3" /> No specialty:
                  </span>
                  <span className="font-medium text-orange-600">{status.cases_no_specialty}</span>
                </div>
              )}
              {status.cases_no_vet > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-orange-600 flex items-center gap-1">
                    <AlertTriangle className="size-3" /> No vet assigned:
                  </span>
                  <span className="font-medium text-orange-600">{status.cases_no_vet}</span>
                </div>
              )}
              {status.cases_routing_conflicts > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-primary flex items-center gap-1">
                    <GitMerge className="size-3" /> Routing conflicts:
                  </span>
                  <span className="font-medium text-primary">{status.cases_routing_conflicts}</span>
                </div>
              )}
            </div>
            {status.cases_no_vet > 0 && (
              <Button
                variant="default"
                className="mt-4 w-full"
                onClick={() => rerouteMutation.mutate()}
                disabled={rerouteMutation.isPending}
              >
                {rerouteMutation.isPending ? 'Routing...' : (
                  <span className="flex items-center gap-1.5">
                    <GitMerge className="size-3.5" /> Re-route Cases
                  </span>
                )}
              </Button>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <h3 className="text-md font-medium text-foreground mb-3 flex items-center gap-1.5">
              <Settings className="size-4" /> Celery Worker
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Pending tasks:</span>
                <span className="font-medium text-foreground">{status.celery_pending_tasks}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <h3 className="text-md font-medium text-foreground mb-3 flex items-center gap-1.5">
              <Wrench className="size-4" /> Actions
            </h3>
            <p className="text-sm text-muted-foreground">
              Use buttons above to trigger corrective actions when issues are detected.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
